import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

function buildPartnerFilter(partnerId: string, partnerName: string, type: "proveedor" | "cliente") {
  if (type === "cliente") return `cliente_id.eq.${partnerId}`;
  return `proveedor_id.eq.${partnerId},proveedor.ilike.%${partnerName}%`;
}

export function usePartnerStats(partnerId: string | null, partnerName: string, type: "proveedor" | "cliente") {
  const { orgId } = useAuth();

  return useQuery({
    queryKey: ["partner-stats", partnerId, type],
    enabled: !!partnerId && !!orgId,
    queryFn: async () => {
      const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString();
      const orFilter = buildPartnerFilter(partnerId!, partnerName, type);

      const { count: opsCount } = await supabase
        .from("embarques")
        .select("id", { count: "exact", head: true })
        .eq("org_id", orgId!)
        .or(orFilter)
        .gte("created_at", yearStart);

      const { data: embarques } = await supabase
        .from("embarques")
        .select("id, created_at, fecha_llegada_real, fecha_llegada_estimada")
        .eq("org_id", orgId!)
        .or(orFilter);

      let avgDays: number | null = null;
      if (embarques && embarques.length > 0) {
        const diffs = embarques
          .map((e) => {
            const arrival = (e as any).fecha_llegada_real || e.fecha_llegada_estimada;
            if (!arrival) return null;
            const diff = (new Date(arrival).getTime() - new Date(e.created_at).getTime()) / (1000 * 60 * 60 * 24);
            return diff > 0 ? diff : null;
          })
          .filter((d): d is number => d !== null);
        if (diffs.length > 0) {
          avgDays = Math.round(diffs.reduce((a, b) => a + b, 0) / diffs.length);
        }
      }

      const embIds = embarques?.map((e) => e.id) || [];
      let pendingDocs = 0;
      if (embIds.length > 0) {
        const { count } = await supabase
          .from("registro_documentos")
          .select("id", { count: "exact", head: true })
          .eq("org_id", orgId!)
          .in("embarque_id", embIds)
          .eq("estado", "Pendiente");
        pendingDocs = count || 0;
      }

      return { opsCount: opsCount || 0, avgDays, pendingDocs };
    },
  });
}

export function usePartnerOperations(partnerId: string | null, partnerName: string, type: "proveedor" | "cliente") {
  const { orgId } = useAuth();

  return useQuery({
    queryKey: ["partner-ops", partnerId, type],
    enabled: !!partnerId && !!orgId,
    queryFn: async () => {
      const orFilter = buildPartnerFilter(partnerId!, partnerName, type);
      const { data } = await supabase
        .from("embarques")
        .select("id, numero_do, estado_logistico, created_at, proveedor, fecha_llegada_estimada, fecha_llegada_real")
        .eq("org_id", orgId!)
        .or(orFilter)
        .order("created_at", { ascending: false })
        .limit(10);

      return data || [];
    },
  });
}

export function usePartnerActivity(partnerId: string | null, partnerName: string, type: "proveedor" | "cliente") {
  const { orgId } = useAuth();

  return useQuery({
    queryKey: ["partner-activity", partnerId, type],
    enabled: !!partnerId && !!orgId,
    queryFn: async () => {
      const orFilter = buildPartnerFilter(partnerId!, partnerName, type);
      const { data: embs } = await supabase
        .from("embarques")
        .select("id")
        .eq("org_id", orgId!)
        .or(orFilter);

      const ids = embs?.map((e) => e.id) || [];
      if (ids.length === 0) return [];

      const { data: docs } = await supabase
        .from("registro_documentos")
        .select("id, no_documento, tipo_documento, estado, created_at")
        .eq("org_id", orgId!)
        .in("embarque_id", ids)
        .order("created_at", { ascending: false })
        .limit(6);

      return docs || [];
    },
  });
}

// New: Partner master documents (documentos_socios table)
export function usePartnerDocuments(partnerId: string | null, type: "proveedor" | "cliente") {
  const { orgId } = useAuth();

  return useQuery({
    queryKey: ["partner-documents", partnerId, type],
    enabled: !!partnerId && !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from("documentos_socios" as any)
        .select("*")
        .eq("org_id", orgId!)
        .eq("socio_id", partnerId!)
        .eq("tipo_socio", type)
        .order("created_at", { ascending: false });

      return (data as any[]) || [];
    },
  });
}

export function useUploadPartnerDocument() {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      socioId,
      tipoSocio,
      file,
      nombreArchivo,
      fechaVencimiento,
    }: {
      socioId: string;
      tipoSocio: "proveedor" | "cliente";
      file: File;
      nombreArchivo: string;
      fechaVencimiento?: string;
    }) => {
      const filePath = `${orgId}/${socioId}/${Date.now()}_${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("documentos_partners")
        .upload(filePath, file);
      if (uploadError) throw uploadError;

      const { error: insertError } = await supabase
        .from("documentos_socios" as any)
        .insert({
          org_id: orgId!,
          socio_id: socioId,
          tipo_socio: tipoSocio,
          nombre_archivo: nombreArchivo,
          archivo_url: filePath,
          fecha_vencimiento: fechaVencimiento || null,
        } as any);
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-documents"] });
    },
  });
}

export function useUpdatePartnerDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, nombreArchivo, fechaVencimiento }: { id: string; nombreArchivo: string; fechaVencimiento?: string | null }) => {
      const { error } = await supabase
        .from("documentos_socios")
        .update({
          nombre_archivo: nombreArchivo,
          fecha_vencimiento: fechaVencimiento || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-documents"] });
    },
  });
}

export function useDeletePartnerDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, archivoUrl }: { id: string; archivoUrl: string }) => {
      await supabase.storage.from("documentos_partners").remove([archivoUrl]);
      const { error } = await supabase
        .from("documentos_socios" as any)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-documents"] });
    },
  });
}
